import { NumberInput, Tooltip, UnstyledButton } from "@mantine/core";
import { AudioLines, ChevronDown, Info } from "lucide-react";
import { useState } from "react";

import type { CatalogAudioModel, Voice } from "../../../../api/generated/model";
import { VoicePanel } from "../../../../platform/voices/VoicePanel";
import { voiceOf, type Cast } from "./cast";
import classes from "./talking-head.module.css";

/** Live's tooltip on Stability (`voice-advanced-settings`). */
const STABILITY_TIP = "Stability between 0-1";

export interface CastVoiceProps {
  teamId: string;
  brandId: string;
  cast: Cast;
  /** The actor's own voice as the voices list has it: live's "Recommended Voice". */
  recommended: Voice | null;
  designModel: CatalogAudioModel | undefined;
  onVoice: (voice: Voice) => void;
  onStability: (stability: number | null) => void;
  onClose: () => void;
}

/**
 * Live's Voice Settings for one actor (`side-panel/voice-settings-form`, L09g): the actor's
 * portrait and its voice at the top, live's voice picker with the actor's own voice as
 * "Recommended Voice", and "Advanced", closed, with the actor's Stability.
 */
export function CastVoice(props: CastVoiceProps) {
  const { teamId, brandId, cast, recommended, designModel, onVoice, onStability, onClose } = props;
  const voice = voiceOf(cast);
  const chosen = cast.picked ?? (recommended !== null && voice?.id === recommended.id ? recommended : null);
  return (
    <VoicePanel
      teamId={teamId}
      brandId={brandId}
      chosen={chosen}
      recommended={recommended}
      onChoose={onVoice}
      onClose={onClose}
      designModel={designModel}
      head={
        <div className={classes.voiceHead}>
          {cast.actor.imageUrl === null ? null : (
            <img className={classes.voiceHeadPortrait} src={cast.actor.imageUrl} alt="" />
          )}
          {voice === null ? (
            <span className={classes.voiceHeadEmpty}>Select Voice</span>
          ) : (
            <span className={classes.voiceHeadName}>
              <AudioLines size={16} aria-hidden />
              <span>{voice.name}</span>
            </span>
          )}
        </div>
      }
      foot={<Stability value={cast.stability} onChange={onStability} />}
    />
  );
}

/** Live's "Advanced" at the panel's foot: closed, then Stability from 0 to 1 by tenths (the Talking
 * Head's and Lip-Sync's Voice Settings). */
export function Stability({ value, onChange }: { value: number | null; onChange: (value: number | null) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={classes.advanced}>
      <UnstyledButton
        className={classes.advancedHead}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span>Advanced</span>
        <ChevronDown size={16} className={classes.chevron} data-open={open || undefined} aria-hidden />
      </UnstyledButton>
      {open ? (
        <div className={classes.advancedBody}>
          <span className={classes.advancedLabel}>
            Stability
            <Tooltip label={STABILITY_TIP} withArrow>
              <Info size={14} aria-label={STABILITY_TIP} />
            </Tooltip>
          </span>
          <NumberInput
            aria-label="Stability"
            className={classes.stability}
            min={0}
            max={1}
            step={0.1}
            decimalScale={1}
            allowNegative={false}
            clampBehavior="strict"
            value={value ?? ""}
            onChange={(next) => onChange(typeof next === "number" ? next : null)}
          />
        </div>
      ) : null}
    </div>
  );
}
